import { useCallback } from 'react';

import { useEventsContext } from '#src/contexts/events/index.ts';
import { useGetProtoSport } from '#src/hooks/events/useGetProtoSport.ts';
import { useGetInteractionContext } from '#src/hooks/useGetInteractionContext.ts';
import { useIsMobileApp } from '#src/hooks/useIsMobileApp.ts';
import { mapKeys } from '#src/lib/object.ts';
import { toStartCase } from '#src/lib/string/toStartCase.ts';
import { toSnakeCase } from '#src/lib/toSnakeCase.ts';
import * as platformEvents from '#src/protos/events/platform/web/events.ts';
import { sportToJSON } from '#src/protos/events/shared/events.ts';
import * as so5SharedEvents from '#src/protos/events/so5/shared/events.ts';
import * as so5Events from '#src/protos/events/so5/web/events.ts';
import type { StartCase, StringKeysOf } from '#src/types/index.ts';

import type { EventsType, WithOptionalCommonProperties } from './EventsType.ts';

type ObjectWithToJSON = { toJSON: (args: never) => unknown };

type PropertiesWithToJSON<T extends object> = {
  [P in StringKeysOf<T> as T[P] extends ObjectWithToJSON
    ? StartCase<P>
    : never]: T[P];
};

const getTypedProtosEvents = <T extends object>(
  protosEvents: T
): PropertiesWithToJSON<T> => {
  const protoEventKeys = Object.keys(protosEvents) as StringKeysOf<
    typeof protosEvents
  >[];
  return protoEventKeys.reduce<PropertiesWithToJSON<T>>((acc, cur) => {
    const currentValue = protosEvents[cur];
    if (Object.prototype.hasOwnProperty.call(currentValue, 'toJSON')) {
      // @ts-expect-error hasOwnProperty is not helpful to narrow the type of cur down
      acc[toStartCase(cur)] = currentValue.toJSON;
    }
    return acc;
  }, {} as PropertiesWithToJSON<T>);
};

const protosEvents = {
  ...getTypedProtosEvents(so5SharedEvents),
  ...getTypedProtosEvents(so5Events),
  ...getTypedProtosEvents(platformEvents),
};

type ProtoEventTypes = typeof protosEvents;
type ProtoTrackEventTypes = {
  [key in keyof ProtoEventTypes]: WithOptionalCommonProperties<
    Parameters<ProtoEventTypes[key]['toJSON']>[0]
  >;
};

enum SourceName {
  MOBILE_APP_IOS = 'mobile_app_ios',
  MOBILE_APP_ANDROID = 'mobile_app_android',
}

type AggregatedTrackEvents = EventsType & ProtoTrackEventTypes;

export const useEvents = () => {
  const { track: baseTrack } = useEventsContext();
  const { isIosApp, isAndroidApp } = useIsMobileApp();
  const getInteractionContext = useGetInteractionContext();

  const sourceName = (() => {
    if (isIosApp) return SourceName.MOBILE_APP_IOS;
    if (isAndroidApp) return SourceName.MOBILE_APP_ANDROID;
    return null;
  })();
  const getSport = useGetProtoSport();

  const track = useCallback(
    <K extends keyof AggregatedTrackEvents>(
      event: K,
      ...rest: AggregatedTrackEvents[K] extends undefined
        ? []
        : [AggregatedTrackEvents[K]]
    ): void => {
      if (Object.prototype.hasOwnProperty.call(protosEvents, event)) {
        baseTrack(event, {
          ...(sourceName && { source_name: sourceName }),
          interaction_context: getInteractionContext(),
          ...(rest[0] &&
            // if it's a protobuf event, let's use its generated transformation function
            // which transform keys to snake case & rounds numbers
            // @ts-expect-error unresolved key issue
            protosEvents[event]({
              interactionContext: getInteractionContext(),
              ...rest[0],
            })),
          // @ts-expect-error unresolved key issue
          sport: rest[0]?.sport || sportToJSON(getSport()),
        });
        return;
      }

      const snakeCaseProperties = rest[0]
        ? mapKeys(rest[0], v => {
            return toSnakeCase(v as string);
          })
        : {};

      baseTrack(event as string, {
        ...(sourceName && { source_name: sourceName }),
        interaction_context: getInteractionContext(),
        sport: sportToJSON(getSport()),
        ...snakeCaseProperties,
      });
    },
    [baseTrack, getSport, getInteractionContext, sourceName]
  );

  return track;
};
